import { useContext, useState, useEffect } from "react";
import axios from "axios";
import { AiOutlineHeart, AiFillHeart } from "react-icons/ai";
import { Link } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { UserContext } from "../Authcontext";
import { API_BASE_URL } from "../api_config";

export default function SearchFilter({ filterData }) {
  const { token, likedProfiles, setLikedProfiles, fetchUserData } = useContext(UserContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const profilesPerPage = 12;

  useEffect(() => {
    // Calculate total pages when filterData changes
    if (filterData && filterData.length > 0) {
      setTotalPages(Math.ceil(filterData.length / profilesPerPage));
      // Reset to page 1 when new search results come in
      setCurrentPage(1);
    } else {
      setTotalPages(1);
    }
  }, [filterData]);

  const isProfileLiked = (userId) => {
    return likedProfiles !== undefined && likedProfiles.includes(userId);
  };

  const handleLikeDislike = (userId, event) => {
    // Prevent the click from navigating to the profile
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (isProfileLiked(userId)) {
      // Dislike the profile
      const updatedLikedProfiles = likedProfiles.filter((id) => id !== userId);
      setLikedProfiles(updatedLikedProfiles);
      axios
        .put(
          `${API_BASE_URL}/api/dislike-profile/${userId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        )
        .then((res) => {
          fetchUserData()
        })
        .catch((error) => {
          // Handle error if needed
        });
    } else {
      // Like the profile
      const updatedLikedProfiles = [...likedProfiles, userId];
      setLikedProfiles(updatedLikedProfiles);
      axios
        .put(
          `${API_BASE_URL}/api/update-like-profiles`,
          { likedProfiles: updatedLikedProfiles },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        )
        .then((res) => {
          fetchUserData()
        })
        .catch((error) => {
          // Handle error if needed
        });
    }
  };

  // Get current profiles for pagination
  const getCurrentProfiles = () => {
    const indexOfLastProfile = currentPage * profilesPerPage;
    const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
    return filterData.slice(indexOfFirstProfile, indexOfLastProfile);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  function renderSearchFilterData() {
    const currentProfiles = getCurrentProfiles();
    
    if (currentProfiles.length === 0) {
      return (
        <div className="modern-no-results">
          <div className="modern-no-results-icon">
            <CgProfile />
          </div>
          <h3>No profiles found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      );
    }
    
    return currentProfiles.map((prof, i) => (
      <li style={{height: "300px"}} className="modern-profile-card" key={i}>
        <Link 
          to={"/view-profile"} 
          state={{ userId: prof.user_id }}          className="modern-profile-card-link"
        >
          <div className="modern-profile-card-inner" >
            <div  className="modern-profile-image-container" >
              {prof.filename === null ? (
                <div className="modern-profile-placeholder">
                  <CgProfile className="modern-profile-placeholder-icon" />
                </div>
              ) : (
                <img
                  src={`${API_BASE_URL}/uploads/` + prof.filename}
                  alt={`${prof.first_name}`}
                  className="modern-profile-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/default-avatar.png";
                  }}
                />
              )}
              <div 
                className="modern-profile-like-button"
                onClick={(e) => handleLikeDislike(prof.user_id, e)}
              >
                {isProfileLiked(prof.user_id) ? (
                  <AiFillHeart
                    className="modern-like-icon liked"
                    size={24}
                  />
                ) : (
                  <AiOutlineHeart
                    className="modern-like-icon"
                    size={24}
                  />
                )}
              </div>
              <div className="modern-profile-overlay">
                <h3 className="modern-profile-overlay-name">{prof.first_name} {prof.last_name}</h3>
              </div>
            </div>
            <div className="modern-profile-details">
              <div className="modern-profile-info-grid">
               <div style={{display: "flex"}}>
               <div className="modern-profile-info-item">
                  <span className="info-label">Age</span>
                  <span className="info-value">{prof.age}</span>
                </div>
                <div className="modern-profile-info-item">
                  <span className="info-label">Education</span>
                  <span className="info-value">{prof.education || "Not specified"}</span>
                </div>
               </div>

                <div style={{display: "flex"}}>
                <div className="modern-profile-info-item">
                  <span className="info-label">Occupation</span>
                  <span className="info-value">{prof.occupation || "Not specified"}</span>
                </div>
                <div className="modern-profile-info-item">
                  <span className="info-label">Location</span>
                  <span className="info-value">{prof.city_name || "Not specified"}{prof.city_name && prof.state_name ? `, ${prof.state_name}` : ""}</span>
                </div>
                </div>
              </div>
              <div className="modern-view-profile-button">
                View Profile
              </div>
            </div>
          </div>
        </Link>
      </li>
    ));
  }

  // Render pagination component
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="search-pagination">
        <div 
          className={`search-pagination-arrow ${currentPage === 1 ? 'disabled' : ''}`}
          onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
        >
          <FiChevronLeft />
        </div>
        
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          let pageNumber;
          
          // Simple pagination for 5 or fewer pages
          if (totalPages <= 5) {
            pageNumber = i + 1;
          } 
          // Complex pagination for more than 5 pages
          else {
            if (currentPage <= 3) {
              // Near the start
              pageNumber = i + 1;
            } else if (currentPage >= totalPages - 2) {
              // Near the end
              pageNumber = totalPages - 4 + i;
            } else {
              // In the middle
              pageNumber = currentPage - 2 + i;
            }
          }
          
          return (
            <div 
              key={`page-${pageNumber}`} 
              className={`search-pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
              onClick={() => handlePageChange(pageNumber)}
            >
              {pageNumber}
            </div>
          );
        })}
        
        <div 
          className={`search-pagination-arrow ${currentPage === totalPages ? 'disabled' : ''}`}
          onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
        >
          <FiChevronRight />
        </div>
      </div>
    );
  };

  return (
    <div className="search-results-container">
      {filterData && filterData.length > 0 ? (
        <>
          <div className="search-results-count">
            Found <span style={{color: "var(--primary-color)"}}>{filterData.length}</span> profiles
          </div>
          <ul className="modern-profile-grid">
            {renderSearchFilterData()}
          </ul>
          {renderPagination()}
        </>
      ) : (
        <div className="modern-no-results">
          <div className="modern-no-results-icon">
            <CgProfile />
          </div>
          <h3>No profiles found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      )}
    </div>
  );
}
